import api from './ApiService';
import { collectorRequest, collectorWrite, type Spot, type SpotFields } from './api/collector';
export type SpotDraft = SpotFields & {
  id: string;
  userId: number;
  photo: Blob;
  capture_source: 'camera' | 'library';
  updatedAt: number;
  readyToSync?: boolean;
  group_entry_key?: string;
};
const DB_NAME = 'bilt-private-spot-drafts';
function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore('drafts', { keyPath: 'id' }).createIndex('userId', 'userId');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(new Error('This device could not keep a draft. Free some storage and try again.'));
  });
}
async function transaction<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction('drafts', mode);
      const request = work(tx.objectStore('drafts'));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = tx.onabort = () =>
        reject(new Error('Draft could not be kept on this device. Free some storage and retry.'));
    });
  } finally {
    db.close();
  }
}
export async function saveSpotDraft(draft: SpotDraft): Promise<void> {
  await transaction('readwrite', (store) => store.put(draft));
}
export async function listSpotDrafts(userId: number): Promise<SpotDraft[]> {
  return (await transaction('readonly', (store) => store.index('userId').getAll(userId))).sort(
    (a, b) => b.updatedAt - a.updatedAt
  );
}
export async function removeSpotDraft(id: string, userId: number): Promise<void> {
  const drafts = await listSpotDrafts(userId);
  if (drafts.some((d) => d.id === id)) await transaction('readwrite', (store) => store.delete(id));
}
export function assertDraftAccount(draft: SpotDraft): void {
  if (Number(api.authTokens?.user?.id) !== draft.userId)
    throw new Error('Sign back in to the account that saved this draft.');
}
const uploads = new Map<string, Promise<Spot>>();
export function uploadSpotDraft(draft: SpotDraft): Promise<Spot> {
  assertDraftAccount(draft);
  const key = `${draft.userId}:${draft.id}`;
  const previous = uploads.get(key);
  if (previous) return previous;
  const upload = (async () => {
    const form = new FormData();
    form.append('image', draft.photo, 'spot.jpg');
    for (const field of [
      'title',
      'make',
      'model',
      'generation',
      'event_context',
      'note',
      'occurred_at',
      'capture_source',
    ] as const)
      form.append(field, draft[field]);
    form.append('client_id', draft.id);
    assertDraftAccount(draft);
    let spot = await collectorRequest<Spot>('spot', { method: 'POST', body: form });
    assertDraftAccount(draft);
    const fields = Object.fromEntries(
      ['title', 'make', 'model', 'generation', 'event_context', 'note', 'occurred_at'].map(
        (key) => [key, draft[key as keyof SpotFields]]
      )
    );
    spot = await collectorWrite<Spot>(
      'spot',
      { ...fields, ...(draft.group_entry_key ? { group_entry_key: draft.group_entry_key } : {}) },
      'PATCH',
      spot.id
    );
    assertDraftAccount(draft);
    await removeSpotDraft(draft.id, draft.userId);
    return spot;
  })().finally(() => uploads.delete(key));
  uploads.set(key, upload);
  return upload;
}

export async function syncSubmittedSpotDrafts(userId: number): Promise<number> {
  const settings = await collectorRequest<{ photo_writes_enabled?: boolean; photo_retired?: boolean }>('settings');
  if (settings.photo_retired || settings.photo_writes_enabled === false) return 0;
  let saved = 0;
  for (const draft of await listSpotDrafts(userId)) {
    if (!draft.readyToSync) continue;
    assertDraftAccount(draft);
    try {
      await uploadSpotDraft(draft);
      saved += 1;
    } catch {
      break;
    }
  }
  return saved;
}

/** Account deletion removes this account's local captures without touching other signed-in accounts. */
export async function clearSpotDrafts(userId: number): Promise<void> {
  const keys = await transaction('readonly', (store) => store.index('userId').getAllKeys(userId));
  for (const key of keys) await transaction('readwrite', (store) => store.delete(key));
}
